import isNumber from 'lodash/isNumber';
import { DateTime } from 'luxon';

import {
  DEFAULT_LISTING_IMAGE,
  ListingStates,
} from '../../components/exclusives/constants/constants';
import {
  ExclusiveListingState,
  ListingType,
  PriorListingPhotoData,
} from '../../declarations/exclusives/exclusiveListingInfo';
import {
  Listing,
  ListingPhoto,
  ListingWithComputedProperties,
  ListingWithDaysRemaining,
  ListingWithListingStates,
  SimilarListing,
} from '../../declarations/exclusives/listing';
import { remainingDaysRoundedToEndTime } from './exclusiveListingInfo';

export function getListingStreet(listing: Listing) {
  return listing.address.street ?? '';
}

export function getListingAddressHumanReadable(listing: Listing) {
  return listing.address.human_readable ?? '';
}

// TODO: @exclusives move this field to be computed server side
export function getListingState(listing: Listing | SimilarListing): ListingStates {
  if (listing.exclusive_listing_info.state === ExclusiveListingState.AwaitingSellerCommitment) {
    return ListingStates.EL_AWAITING_SELLER_COMMITMENT;
  }

  if (listing.exclusive_listing_info.state === ExclusiveListingState.Setup) {
    return ListingStates.EL_SETUP;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.InContract) {
    return ListingStates.EL_IN_CONTRACT;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.Sold) {
    return ListingStates.EL_SOLD;
  }

  if (listing.exclusive_listing_info.state == ExclusiveListingState.Expired) {
    return ListingStates.EL_EXPIRED;
  }

  if (
    listing.exclusive_listing_info.reservation_end_time &&
    !listing.exclusive_listing_info.reservation_locked
  ) {
    return ListingStates.RESERVATION_PENDING;
  }

  if (
    listing.exclusive_listing_info.state == 'el_listed' &&
    listing.exclusive_listing_info.reservation_locked !== true
  ) {
    return ListingStates.EL_LISTED;
  }

  if (listing.exclusive_listing_info.reservation_locked === true) {
    return ListingStates.EL_RESERVATION_LOCKED;
  }

  // safe default to allow adding to waitlist
  return ListingStates.EL_SETUP;
}

export function getIsSellerOfferExperiment(listing: Listing) {
  return (
    !getIsOOMLS(getListingWithListingStates(listing)) &&
    Boolean(listing.exclusive_listing_info?.seller_offer_experiment)
  );
}

export function getIsSellerOwnedOpendoorPriced(listing: Listing) {
  return Boolean(listing.exclusive_listing_info?.seller_owned_with_opendoor_set_price);
}

export function getListingWithListingStates(listing: Listing): ListingWithListingStates {
  return {
    ...listing,
    // TODO: @exclusives move this field to be computed server side
    el_state: getListingState(listing),
  };
}

export function getHasOngoingOrUpcomingOpenHouses(listing: Listing) {
  return !!listing.exclusive_listing_info?.ongoing_and_upcoming_open_houses?.length;
}

export function getIsExpiringSoon(listing: Listing) {
  return listing.exclusive_listing_info.end_time
    ? DateTime.fromISO(listing.exclusive_listing_info.end_time) < DateTime.now().plus({ days: 6 })
    : false;
}

export function getIsPostOpenHouse(
  listing: ListingWithComputedProperties,
  hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing),
  expiringSoon = getIsExpiringSoon(listing),
) {
  return (
    listing.el_state === ListingStates.EL_LISTED && !hasOngoingOrUpcomingOpenHouses && expiringSoon
  );
}

export function getIsOpenHouseSchedulingComingSoon(
  listing: ListingWithComputedProperties,
  isPostOpenHouse = getIsPostOpenHouse(listing),
  hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing),
) {
  return !isPostOpenHouse && !hasOngoingOrUpcomingOpenHouses;
}

/** Gets a thumbnail image from listing or fallback to default */
export function getListingThumbnail(listing: Listing, key: keyof ListingPhoto['thumbnail_urls']) {
  if (!listing) return DEFAULT_LISTING_IMAGE;
  return (
    getListingPhotos(listing)?.[0]?.thumbnail_urls?.[key] ||
    listing.exclusive_listing_info?.local_area_map_image ||
    DEFAULT_LISTING_IMAGE
  );
}

/** Map any static image url to a listing photo object */
function mapStaticImageToListingPhoto(url: string): ListingPhoto {
  return {
    url: url,
    thumbnail_urls: {
      _375x250: url,
      _768x512: url,
      _1200x800: url,
      _1800x1200: url,
    },
  };
}

/** Gets listing photos or fallback to defaults */
export function getListingPhotos(listing: Listing): ListingPhoto[] {
  if (listing.listing_photos?.length) {
    return listing.listing_photos;
  }

  if (
    getIsSellerOfferExperiment(listing) &&
    listing.exclusive_listing_info.prior_listing_photo_data?.urls.length
  ) {
    return priorListingPhotoDataIntoListingPhotos(
      listing.exclusive_listing_info.prior_listing_photo_data,
    );
  }

  if (listing.exclusive_listing_info?.local_area_map_image) {
    return [mapStaticImageToListingPhoto(listing.exclusive_listing_info.local_area_map_image)];
  }

  return [mapStaticImageToListingPhoto(DEFAULT_LISTING_IMAGE)];
}

export function priorListingPhotoUrlToListingPhoto(url: string): ListingPhoto {
  const base = `${url}?auto=format,compress&ch=Width,DPR&dpr=2`;
  return {
    is_prior: true,
    url: base,
    thumbnail_urls: {
      _375x250: base + '&fit=crop&w=375&h=250',
      _768x512: base + '&fit=crop&w=768&h=512',
      _1200x800: base + '&fit=crop&w=1200&h=800',
      _1800x1200: base + '&fit=crop&w=1800&h=1200',
    },
  };
}

export function priorListingPhotoDataIntoListingPhotos(
  priorListingPhotoData: PriorListingPhotoData | null | undefined,
): ListingPhoto[] {
  if (!priorListingPhotoData) return [];
  return priorListingPhotoData.urls.map(priorListingPhotoUrlToListingPhoto);
}

export function getPriorListingPhotoDataListedAt(listing: Listing) {
  return listing.exclusive_listing_info.prior_listing_photo_data?.listed_at;
}

export function getListingWithRemainingDays<L extends Listing>(
  listing: L,
): ListingWithDaysRemaining & L {
  const daysRemaining = remainingDaysRoundedToEndTime(listing.exclusive_listing_info);
  return { ...listing, ...(isNumber(daysRemaining) && { days_remaining: daysRemaining }) };
}

export function getListingWithComputedProperties(listing: Listing): ListingWithComputedProperties {
  return getListingWithRemainingDays(getListingWithListingStates(listing));
}

export function getExclusivesEventHomeInfo(listing: Listing) {
  let homeStatus: string;
  switch (listing.exclusive_listing_info.state) {
    case ListingStates.EL_SETUP:
      homeStatus = 'available_soon';
      break;
    case ListingStates.EL_LISTED:
      homeStatus = 'listed';
      break;
    case ListingStates.EL_IN_CONTRACT:
      homeStatus = 'in_contract';
      break;
    case ListingStates.EL_AWAITING_SELLER_COMMITMENT:
      homeStatus = 'unlisted';
      break;
    case ListingStates.EL_SOLD:
      homeStatus = 'sold';
      break;
    default:
      homeStatus = 'unknown';
      break;
  }

  let homeOwner: string;
  switch (listing.exclusive_listing_info.listing_type) {
    case ListingType.SELLER_OWNED_EXCLUSIVE:
      homeOwner = 'available_soon';
      break;
    case ListingType.OD_OWNED_EXCLUSIVE:
      homeOwner = 'listed';
      break;
    case ListingType.OOMLS:
      homeOwner = 'in_contract';
      break;
    default:
      homeOwner = 'unknown';
      break;
  }

  return {
    home_owner: homeOwner,
    home_status: homeStatus,
  };
}

export const DISCOUNT_FOR_SELLER_OWNED_BUYERS_IN_PERCENTAGE = 0.5;

export function getListingPriceCents(
  listing: Listing,
  isSellerOfferExperiment: boolean = getIsSellerOfferExperiment(listing),
) {
  if (isSellerOfferExperiment && getIsExpired(getListingWithComputedProperties(listing))) {
    // When listing.price_cents does not exist, it gets serialized to 0
    return 0;
  }
  return listing.exclusive_listing_info.exclusives_listing_price_cents || listing.price_cents;
}

export function getOriginalListingPriceCents(
  listing: Listing,
  isSellerOfferExperiment: boolean = getIsSellerOfferExperiment(listing),
) {
  if (
    listing.exclusive_listing_info.mls_listing_price_cents ===
    listing.exclusive_listing_info.exclusives_listing_price_cents
  ) {
    return null;
  }
  if (isSellerOfferExperiment && getIsExpired(getListingWithComputedProperties(listing))) {
    return null;
  }
  return listing.exclusive_listing_info.mls_listing_price_cents;
}

const TOURABLE_LISTING_STATES = [
  ListingStates.RESERVATION_PENDING,
  ListingStates.EL_LISTED,
  ListingStates.EL_IN_CONTRACT,
];

export function getIsListingTourable(listing: ListingWithComputedProperties) {
  return !getIsSellerOfferExperiment(listing) && TOURABLE_LISTING_STATES.includes(listing.el_state);
}

export function getIsOOMLS(listing: ListingWithComputedProperties): boolean {
  return listing.exclusive_listing_info.listing_type === ListingType.OOMLS;
}

export function getIsInContract(listing: ListingWithComputedProperties) {
  return [ListingStates.EL_IN_CONTRACT].includes(listing.el_state);
}

export function getIsAwaitingSellerCommitment(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_AWAITING_SELLER_COMMITMENT;
}

export function getIsExpired(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_EXPIRED;
}

export function getIsAvailableSoon(listing: ListingWithComputedProperties) {
  return listing.el_state === ListingStates.EL_SETUP;
}

export function getBallparkPriceRange(listing: Listing) {
  return listing.exclusive_listing_info.ballpark_price_range;
}

const ACTIVE_ELI_STATES: Listing['exclusive_listing_info']['state'][] = [
  ExclusiveListingState.Listed,
  ExclusiveListingState.Setup,
];

export function getActiveListings(listings: ListingWithComputedProperties[]) {
  return listings.filter((l) => ACTIVE_ELI_STATES.includes(l.exclusive_listing_info.state));
}

export function shouldShowPrelimOfferPriceRange(listing: ListingWithComputedProperties) {
  return (
    getIsSellerOfferExperiment(listing) &&
    getIsAvailableSoon(listing) &&
    listing.exclusive_listing_info.prelim_offer_price_range
  );
}
